import React, { memo, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getPanelElement, ImperativePanelHandle, Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';

import ChevronLeft from '@/assets/icons/ChevronLeft';
import ChevronRight from '@/assets/icons/ChevronRight';
import { SidePanelContextMenuInformation } from '@/components/contextMenu/mwdContextMenu/types';
import { SidebarContextMenu } from '@/components/contextMenu/sidebarContextMenu/SidebarContextMenu';
import DetailPanel from '@/components/detailPanel/DetailPanel';
import MaterieelPanel from '@/components/detailPanel/materieelDetails/MaterieelPanel';
import { AdvancedSearchData } from '@/components/search/types';
import Conflicten from '@/components/sidebar/conflicten/Conflicten';
import Defecten from '@/components/sidebar/defecten/Defecten';
import SidebarPanelErrorBoundary from '@/components/sidebar/SidebarPanelErrorBoundary/SidebarPanelErrorBoundary';
import Signalen from '@/components/sidebar/signalen/Signalen';
import { useWriteAndReadCssProperty } from '@/hooks/useCssProperty';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import useClientState from '@/state/useClientState';

const DetailPanelMemo = memo(DetailPanel);
const MaterieelPanelMemo = memo(MaterieelPanel);

import styles from './SidebarWithMainContentPanels.module.css';

type SidebarWithMainContentPanelsProps = {
  handleSelectieUitPanel: (advancedSearchData: AdvancedSearchData[]) => void;
  children: React.ReactNode;
  activePanel?: string;
  setActivePanel: (activePanel: 'Conflicten' | 'Defecten' | 'Signalen' | 'Mwd' | undefined) => void;
};

const SIDE_PANEL_COLLAPSED_KEY = 'mwd:collapsed-left-panel';
const NARROW_CONTAINER_WIDTH = 1260;

export const saveCollapsed = (saved: boolean) => localStorage?.setItem(SIDE_PANEL_COLLAPSED_KEY, saved ? '1' : '0');

export const readCollapsed = (): boolean => localStorage?.getItem(SIDE_PANEL_COLLAPSED_KEY) === '1';

const SidebarWithMainContentPanels = ({
  children,
  handleSelectieUitPanel,
  activePanel,
  setActivePanel,
  ...restProps
}: SidebarWithMainContentPanelsProps): ReactElement => {
  const [setSidebarWidth, setSidebarWidthRef, sidebarWidth] = useWriteAndReadCssProperty('--sidebar-width');
  const [sidebarHeight, setSidebarHeight] = useState(0);

  const resizeCallback = useCallback(
    (bb: { width: number; height: number }) => {
      setSidebarWidth(bb.width);
      setSidebarHeight(bb.height);
    },
    [setSidebarWidth],
  );
  const leftPanelRef = useResizeObserver(resizeCallback);
  const [contextMenuInformation, setContextMenuInformation] = useState<SidePanelContextMenuInformation>();
  const showDetailPanel = useClientState((state) => state.showDetailPanel);
  const showMaterieelPanel = useClientState((state) => state.showMaterieelPanel);
  const [displayNoneDetailPanel, setDisplayNoneDetailPanel] = useState(false);

  const leftImperativeRef = useRef<ImperativePanelHandle | null>(null);
  const [isLeftCollapsed, setIsLeftCollapsed] = useState(false);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);
  const autoCollapseRef = useRef(false);

  const setCollapsed = useCallback((v: boolean) => {
    setIsLeftCollapsed(v);
    if (autoCollapseRef.current) {
      autoCollapseRef.current = false;
      return;
    }
    saveCollapsed(v);
  }, []);

  const toggleLeft = useCallback(() => {
    if (!leftImperativeRef.current) return;
    if (isLeftCollapsed) leftImperativeRef.current.expand();
    else leftImperativeRef.current.collapse();
  }, [isLeftCollapsed]);

  const handleLeftResize = useCallback(() => {
    const size = leftImperativeRef.current?.getSize();
    if (typeof size === 'number') {
      const collapsedNow = size <= 0.5;
      setIsLeftCollapsed(collapsedNow);
    }
  }, []);

  useEffect(() => {
    if (!containerRef) return;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width && width <= NARROW_CONTAINER_WIDTH) {
        autoCollapseRef.current = true;
        leftImperativeRef.current?.collapse();
      }
    });
    observer.observe(containerRef);

    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    const shouldBeCollapsed = readCollapsed();
    if (shouldBeCollapsed) {
      leftImperativeRef.current?.collapse();
      setIsLeftCollapsed(true);
    } else {
      leftImperativeRef.current?.expand?.();
      setIsLeftCollapsed(false);
    }
  }, []);

  useEffect(() => {
    if (!showDetailPanel && !showMaterieelPanel) {
      const t = setTimeout(() => setDisplayNoneDetailPanel(true), 200);
      return () => clearTimeout(t);
    }
    setDisplayNoneDetailPanel(false);
  }, [showDetailPanel, showMaterieelPanel]);

  const contextMenuInformationCallback = useCallback((data: SidePanelContextMenuInformation) => {
    setContextMenuInformation(data);
  }, []);

  const panelMenuInformation = {
    activePanel,
    setActivePanel,
    contextMenuInformationCallback,
    handleSelectieUitPanel,
  };

  useEffect(() => {
    leftPanelRef(getPanelElement('left-panel'));
  }, [leftPanelRef]);

  const sidebarContextMenuMemo = useMemo(
    () => (
      <SidebarContextMenu
        data={contextMenuInformation}
        labels={contextMenuInformation?.labels}
        iconNames={contextMenuInformation?.iconNames}
        handleGeselecteerdUitPanel={handleSelectieUitPanel}
        sidebarWidth={sidebarWidth}
        sidebarHeight={sidebarHeight}
        setActivePanel={setActivePanel}
      />
    ),
    [contextMenuInformation, handleSelectieUitPanel, sidebarWidth, sidebarHeight],
  );

  return (
    <div ref={setContainerRef} className={styles.rootContainer}>
      <PanelGroup className={styles.panelGroupStyle} direction='horizontal' id='side-panel' autoSaveId='mwd-side-panel'>
        <>
          <Panel
            id='left-panel'
            ref={leftImperativeRef}
            className={`${styles.leftPanelStyle} ${isLeftCollapsed ? styles.leftCollapsed : ''}`}
            defaultSize={12}
            minSize={0}
            maxSize={22}
            collapsible
            collapsedSize={0.2}
            onCollapse={() => setCollapsed(true)}
            onExpand={() => setCollapsed(false)}
            onResize={handleLeftResize}
          >
            <div ref={setSidebarWidthRef} className={styles.leftPanelDivStyle}>
              {!isLeftCollapsed && (
                <PanelGroup direction='vertical' autoSaveId='mwd-left-vertical-v1'>
                  <>
                    <Panel id='conflicten-panel' minSize={10} order={1}>
                      <SidebarPanelErrorBoundary title='Conflicten' sectionId='conflicten'>
                        <Conflicten panelMenuInformation={panelMenuInformation} />
                      </SidebarPanelErrorBoundary>
                    </Panel>
                    <PanelResizeHandle className={styles.horizontalResizeHandle} />
                  </>
                  <Panel id='defecten-panel' minSize={10} order={2}>
                    <SidebarPanelErrorBoundary title='Defecten' sectionId='defecten'>
                      <Defecten panelMenuInformation={panelMenuInformation} />
                    </SidebarPanelErrorBoundary>
                  </Panel>
                  <>
                    <PanelResizeHandle className={styles.horizontalResizeHandle} />
                    <Panel id='signalen-panel' minSize={10} order={3}>
                      <SidebarPanelErrorBoundary title='Signalen' sectionId='signalen'>
                        <Signalen panelMenuInformation={panelMenuInformation} />
                      </SidebarPanelErrorBoundary>
                    </Panel>
                  </>
                </PanelGroup>
              )}
              {sidebarContextMenuMemo}
            </div>
          </Panel>

          <PanelResizeHandle className={styles.verticalResizeHandle} />
        </>
        <div className={styles.collapseBtnContainer}>
          <button
            type='button'
            aria-label={isLeftCollapsed ? 'Open sidebar' : 'Close sidebar'}
            className={`${styles.collapseBtn} ${isLeftCollapsed ? styles.isCollapsed : ''}`}
            onClick={toggleLeft}
          >
            {isLeftCollapsed ? (
              <ChevronRight className={styles.chev} width={18} height={18} />
            ) : (
              <ChevronLeft className={styles.chev} width={18} height={18} />
            )}
          </button>
        </div>
        <Panel className={styles.mwdPanelStyle}>
          <div className={`${styles.mwdDivStyle} bam-client-view`} {...restProps}>
            {children}
          </div>
        </Panel>

        <Panel
          id='right-panel'
          style={displayNoneDetailPanel ? { display: 'none' } : undefined}
          className={`${styles.rightPanelStyle} ${showDetailPanel || showMaterieelPanel ? styles.open : styles.hidden}`}
        >
          <div className={styles.rightPanelDivStyle}>
            <PanelGroup direction='vertical'>
              <Panel id='detail-panel'>
                {showDetailPanel && <DetailPanelMemo />}
                {showMaterieelPanel && <MaterieelPanelMemo />}
              </Panel>
            </PanelGroup>
          </div>
        </Panel>
      </PanelGroup>
    </div>
  );
};

export default SidebarWithMainContentPanels;
